import { Action, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ThunkAction } from 'redux-thunk';
import { LantisPageCounterBlModel } from '../../../Restful/LantisApi';
import { msalInstance } from '../../auth';
import { RootState } from '../../core/store';
import { apiCallBegan } from '../../core/store/api/action';
import { filterItemsByPermissions, getCompleteLink } from '../services/MenuService';

export interface NavbarState {
    data: {
        items: LantisPageCounterBlModel[];
    };
    error: string;
    loading: boolean;
    showNavbar: boolean;
}

const initialState: NavbarState = {
    data: {
        items: [],
    },
    error: '',
    loading: false,
    showNavbar: true,
};

const navbarSlice = createSlice({
    name: 'navbar',
    initialState,
    reducers: {
        itemsRequested(state, _) {
            state.loading = true;
            state.error = '';
        },
        itemsReceived(state, action: PayloadAction<LantisPageCounterBlModel[]>) {
            const items = action.payload.map((item) => {
                const directories = item?.iconPath?.split('/');
                if (item.isSharepoint) {
                    if (!item.isExternalLink) {
                        item.link = getCompleteLink(item.link || '');
                        item.isExternalLink = true;
                    }
                }

                return {
                    ...item,
                    iconPath: directories?.pop() as string,
                };
            });
            state.data.items = items;
            state.loading = false;
        },
        itemsRequestFailed(state, action: PayloadAction<string>) {
            state.loading = false;
            state.error = action.payload;
        },
        setShowNavBar(state, action: PayloadAction<boolean>) {
            state.loading = false;
            state.showNavbar = action.payload;
        },
    },
});

export const navbarReducer = navbarSlice.reducer;
export const { itemsRequested, itemsReceived, itemsRequestFailed, setShowNavBar } = navbarSlice.actions;

export type NavbarActionTypes =
    | ReturnType<typeof itemsRequested>
    | ReturnType<typeof itemsReceived>
    | ReturnType<typeof itemsRequestFailed>;

// Selectors
export const applicationsItemsSelector = createSelector(
    (state: RootState) => state.navbar.data.items,
    (state: RootState) => state.user.roles,
    (state: RootState) => state.user.data,
    (state: RootState) => state.navbar.showNavbar,
    (items, roles, user) => filterItemsByPermissions(items, roles, user) as LantisPageCounterBlModel[],
);


// Thunks
export const loadItems = (): ThunkAction<void, RootState, unknown, Action<string>> => (dispatch, getState) => {
    const activeAccount = msalInstance.getActiveAccount();
    return dispatch(
        apiCallBegan({
            url: '/api/LantisPageCounter/GetLantisPageCounter',
            method: 'GET',
            safe: false,
            params: { sharepointId: activeAccount?.localAccountId },
            onStart: itemsRequested.type,
            onSuccess: itemsReceived.type,
            onError: itemsRequestFailed.type,
        }),
    );
};
